import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { selectAutoSave } from "../app/slices/GlobalSlice";
import { selectIsDirty } from "../app/slices/TrainingSlice";
import { useSaveTraining } from "./SaveTrainingHook";

const AUTO_SAVE_INTERVAL_MS = 300_000;

export const useTrainingAutoSave = () => {
  const isDirty = useSelector(selectIsDirty);
  const autoSaveEnabled = useSelector(selectAutoSave);
  const { saveTraining } = useSaveTraining();
  const isSavingRef = useRef(false);
  const isDirtyRef = useRef(isDirty);
  const { t } = useTranslation();

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    if (!autoSaveEnabled) {
      return;
    }

    const autoSave = async () => {
      if (!isDirtyRef.current || isSavingRef.current) {
        return;
      }

      isSavingRef.current = true;
      try {
        const result = await saveTraining({
          mode: "autosave",
          showGlobalLoading: false,
        });

        if (result.success) {
          toast.success(t("project_saved"), { position: "top-right" });
        } else {
          toast.error(t("auto_save_failed"), { position: "top-right" });
        }
      } catch {
        toast.error(t("auto_save_failed"), { position: "top-right" });
      } finally {
        isSavingRef.current = false;
      }
    };

    const intervalId = window.setInterval(() => {
      void autoSave();
    }, AUTO_SAVE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [autoSaveEnabled, saveTraining, t]);
};
